import React, { useState, useRef, useEffect, useCallback } from 'react';
import { message } from 'antd';
import styles from './index.module.less';
import ImageSearchResultSkeleton from '../ImageSearchResult/ImageSearchResultSkeleton';
import ImageSearchResult, { RecentDataType } from '../ImageSearchResult';
import {
  getReverseSearchTaskByIdApi,
  getReverseSearchTaskListApi,
  postReverseSearchTaskApi,
} from '@/lib/service/image-search';
import { useSearchParams } from 'next/navigation';
import { usePolling } from '@/hooks/usePolling';
import { useTranslation } from 'next-i18next';
import { useUserProvider } from '@/context/UserProvider';
import ImageSearchLoading from '../ImageSearchLoading';
import NotFound from '../ImageSearchResult/NotFound';
import ImageSearchReport from '../ImageSearchReport';
import { useImageSearchStore } from '@/store/imageSearchStore';

// 模拟搜索数据
const mockSearchData: RecentDataType = {
  task_id: 1,
  status: 'done',
  email_notify: false,
  search_image: '/images/layouts/erasa.webp',
  payment_url: 'https://www.erasa.net/',
  type: 'image',
  results: [],
};

interface ImageSearchContainerProps {
  isSearching: boolean;
  setIsSearching: (isSearching: boolean) => void;
  onRegisterSearchClick: (fn: (imageUrl: string) => void) => void;
  imageSearchUrl: string;
  setImageSearchUrl: (imageUrl: string) => void;
  contentFunction: string;
  onRemoveImage?: () => void;
  onRegisterRemoveImage?: (fn: () => void) => void;
}

/**
 * 图片搜索容器组件
 * 用于展示图片搜索的结果（报告页和搜索结果页）
 */
const ImageSearchContainer: React.FC<ImageSearchContainerProps> = ({
  isSearching,
  setIsSearching,
  onRegisterSearchClick,
  imageSearchUrl,
  setImageSearchUrl,
  contentFunction,
  onRemoveImage,
  onRegisterRemoveImage,
}) => {
  const { isLogin } = useUserProvider();
  const { t } = useTranslation();
  const searchParams = useSearchParams();
  const { setIsPinned } = useImageSearchStore();
  const resultContainerRef = useRef<HTMLDivElement>(null);
  // 进度条每一项的时长(0 表示使用默认时长)
  const [stepDuration, setStepDuration] = useState(0);
  // 搜索结果数据
  const [searchData, setSearchData] = useState<RecentDataType>(null);
  // 搜索模式
  const [searchMode, setSearchMode] = useState<'low' | 'high'>('low');
  // 是否锁住
  const [isLocked, setIsLocked] = useState(true);
  // 根据 ID 查询的搜索状态
  const [isQueryTaskStatus, setIsQueryTaskStatus] = useState(false);
  // 是否需要支付（用于控制 SendEmailModal 显示）
  const [isNeedPay, setIsNeedPay] = useState(false);
  // 是否显示 Loading 组件（API 请求返回后才显示）
  const [showLoading, setShowLoading] = useState(false);
  // 搜索是否失败
  const [isSearchFailed, setIsSearchFailed] = useState(false);
  // 任务 ID
  const [taskId, setTaskId] = useState<number>(null);
  // 用于轮询时获取最新的 taskId
  const taskIdRef = useRef<number>(null);
  // 搜索报告列表
  const [reportList, setReportList] = useState<any[]>([]);
  // 搜索报告列表加载状态
  const [reportListLoading, setReportListLoading] = useState(false);
  // loading 开始时间
  const loadingStartTimeRef = useRef<number>(0);
  // 最小 loading 持续时间（75秒）
  const MIN_LOADING_DURATION = 75000;

  // 延迟关闭 loading，确保至少显示 MIN_LOADING_DURATION
  const stopLoadingWithMinDuration = useCallback(
    (callback?: () => void) => {
      const elapsed = Date.now() - loadingStartTimeRef.current;
      const remaining = MIN_LOADING_DURATION - elapsed;

      if (remaining > 0) {
        setTimeout(() => {
          setIsSearching(false);
          setShowLoading(false);
          callback?.();
        }, remaining);
      } else {
        setIsSearching(false);
        setShowLoading(false);
        callback?.();
      }
    },
    [setIsSearching],
  );

  // 使用 usePolling hook 处理轮询
  const { start: startPolling } = usePolling({
    fn: async () => {
      const res = await getReverseSearchTaskByIdApi(taskIdRef.current);

      if (res.code === 0 && res.data) {
        const taskData = res.data;
        // API 返回后显示 Loading 组件
        setShowLoading(true);
        setImageSearchUrl(res.data.search_image);

        if (taskData.status === 'need_pay') {
          setIsNeedPay(true);
          stopLoadingWithMinDuration(() => {
            setIsLocked(true);
            setShowLoading(false);
          });
          return { shouldStop: true };
        } else if (taskData.status === 'success') {
          stopLoadingWithMinDuration(() => {
            setIsLocked(false);
            setSearchMode(res.data.type === 'premium' ? 'high' : 'low');
            setShowLoading(false);
            setSearchData(taskData);
            // 判断结果 results 长度为 0，显示空结果页
            if (taskData.results.length <= 0) {
              setIsSearchFailed(true);
            }
          });
          return { shouldStop: true, data: taskData };
        } else if (taskData.status === 'no_result' || taskData.status === 'failed') {
          setIsSearching(false);
          setShowLoading(false);
          setIsSearchFailed(true);
          return { shouldStop: true };
        }
      } else {
        setIsSearching(false);
        setShowLoading(false);
        return { shouldStop: true };
      }

      return { shouldStop: false };
    },
    interval: 2000,
    onError: (error) => {
      message.error(error.message);
      setIsSearching(false);
      setShowLoading(false);
    },
  });

  // 封装开始轮询的方法，设置 taskIdRef 后再启动
  const handleStartPolling = (currentTaskId: number) => {
    taskIdRef.current = currentTaskId;
    startPolling();
  };

  const handleSearchComplete = async (imageUrl: string) => {
    if (isSearching) return;

    // 取消置顶
    setIsPinned(false);
    setIsSearchFailed(false);

    loadingStartTimeRef.current = Date.now();
    setIsSearching(true);
    setStepDuration(reportList.length > 0 ? 0 : 1000);
    setSearchMode('low');

    // 延迟滚动，确保 DOM 已更新
    setTimeout(() => {
      resultContainerRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 100);

    try {
      const res = await postReverseSearchTaskApi({
        image_url: imageUrl,
        is_premium: false,
        redirect: '/dashboard/privacy-protection/portrait',
      });

      if (res.code === 0 && res.data) {
        // 获取任务ID
        const newTaskId = res.data.task_id;
        // API 返回后显示 Loading 组件
        setShowLoading(true);

        // 需要支付
        if (res.data.status === 'need_pay') {
          setIsNeedPay(true);
          setTimeout(() => {
            // 延迟滚动，确保 DOM 已更新
            setTimeout(() => {
              resultContainerRef.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
              });
            }, 100);
            setTaskId(newTaskId);
            setIsLocked(true);
            setIsSearching(false);
            setShowLoading(false);
            setSearchData({
              ...mockSearchData,
              payment_url: res.data.payment_url,
            });
          }, 4000);
          return;
        }

        if (newTaskId) {
          // 默认走的是未付费逻辑
          setSearchMode('low');
          setStepDuration(0);
          setIsLocked(true);
          setTaskId(newTaskId);
          // 开始轮询任务状态
          handleStartPolling(newTaskId);
        }
      } else {
        message.error('Search failed');
        setIsSearching(false);
        setShowLoading(false);
      }
    } catch (error) {
      message.error((error as Error).message);
      setIsSearching(false);
      setShowLoading(false);
    }
  };

  // 注册 handleSearchComplete 函数供外部调用
  React.useEffect(() => {
    onRegisterSearchClick?.(handleSearchComplete);
  }, []);

  // 获取路由参数
  useEffect(() => {
    const f = searchParams.get('f');
    const reportId = searchParams.get('reportId');

    if (f) {
      if (f === 'true' && reportId) {
        // 延迟滚动，确保 DOM 已更新
        setTimeout(() => {
          resultContainerRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }, 100);
        // 记录 loading 开始时间
        loadingStartTimeRef.current = Date.now();
        // 默认走的是未付费逻辑
        setTaskId(Number(reportId));
        setSearchMode('high');
        setStepDuration(0);
        setIsLocked(true);
        setIsSearching(true);
        // 开始轮询任务状态
        handleStartPolling(Number(reportId));
      } else {
        message.error('Pay Error');
      }
      // 删除路由参数
      const url = new URL(window.location.href);
      url.searchParams.delete('f');
      url.searchParams.delete('reportId');
      window.history.replaceState({}, '', url.toString());
      return;
    }

    if (reportId) {
      // 延迟滚动，确保 DOM 已更新
      setTimeout(() => {
        resultContainerRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }, 100);
      setIsQueryTaskStatus(true);
      // 根据任务ID查询任务状态和详情接口
      getReverseSearchTaskByIdApi(Number(reportId))
        .then((res) => {
          if (res.code === 0 && res.data) {
            // 判断任务状态是否为 success
            if (res.data.status === 'success') {
              setTaskId(Number(reportId));
              setIsLocked(false);
              setSearchMode(res.data.type === 'premium' ? 'high' : 'low');
              setSearchData(res.data);
              // 判断结果 results 长度为 0，显示空结果页
              if (res.data.results.length <= 0) {
                setIsSearchFailed(true);
              }
              // 延迟滚动，确保 DOM 已更新
              setTimeout(() => {
                resultContainerRef.current?.scrollIntoView({
                  behavior: 'smooth',
                  block: 'start',
                });
              }, 100);
            } else if (res.data.status === 'no_result' || res.data.status === 'failed') {
              setIsSearching(false);
              setShowLoading(false);
              setIsSearchFailed(true);
            } else {
              message.error(t('Please wait for the report to be completed.'));
            }
          } else {
            message.error(t('The report does not exist.'));
          }
        })
        .finally(() => {
          setIsQueryTaskStatus(false);
        });
      // 删除路由参数
      const url = new URL(window.location.href);
      url.searchParams.delete('reportId');
      window.history.replaceState({}, '', url.toString());
    }
  }, [searchParams]);

  // 返回按钮点击事件
  const handleBackClick = useCallback(() => {
    setSearchData(null);
    setIsLocked(true);
    // 评论设置为置顶
    setIsPinned(false);
    setSearchMode('low');
    setIsQueryTaskStatus(false);
    setIsSearching(false);
    setStepDuration(0);
    setIsNeedPay(false);
    setShowLoading(false);
    setIsSearchFailed(false);
    setImageSearchUrl('');
    // 清除上传组件的图片
    onRemoveImage?.();
    // 移动到页面顶部 view
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [setIsSearching, setImageSearchUrl, onRemoveImage]);

  // 注册移除图片的回调函数
  useEffect(() => {
    onRegisterRemoveImage?.(handleBackClick);
  }, [onRegisterRemoveImage, handleBackClick]);

  // 轮询获取历史列表（当有 processing 状态时持续轮询）
  const { start: startReportListPolling } = usePolling({
    fn: async () => {
      const res = await getReverseSearchTaskListApi();

      if (res.code === 0 && res.data) {
        const filteredReportList = res.data
          .filter((item: any) => item.status === 'success' || item.status === 'processing' || item.status === 'failed')
          .sort((a: any, b: any) => new Date(b.create_time).getTime() - new Date(a.create_time).getTime());
        setReportList(filteredReportList);

        // 检查是否还有 processing 状态的任务
        const hasProcessing = filteredReportList.some((item: any) => item.status === 'processing');

        // 如果没有 processing 状态，停止轮询
        if (!hasProcessing) {
          return { shouldStop: true };
        }
      }

      return { shouldStop: false };
    },
    interval: 2000,
    immediate: false, // 首次获取单独处理，不立即执行
  });

  // 获取历史
  useEffect(() => {
    if (isLogin) {
      setReportListLoading(true);
      getReverseSearchTaskListApi()
        .then((res) => {
          if (res.code === 0 && res.data) {
            const filteredReportList = res.data
              .filter(
                (item: any) => item.status === 'success' || item.status === 'processing' || item.status === 'failed',
              )
              .sort((a: any, b: any) => new Date(b.create_time).getTime() - new Date(a.create_time).getTime());
            setReportList(filteredReportList);

            // 如果有 processing 状态的任务，开始轮询
            const hasProcessing = filteredReportList.some((item: any) => item.status === 'processing');
            if (hasProcessing) {
              startReportListPolling();
            }
          }
        })
        .finally(() => {
          setReportListLoading(false);
        });
    } else {
      handleBackClick();
    }
  }, [isLogin]);

  // 免费模式重新搜索按钮点击事件
  const handleTryAnotherFreeSearch = () => {
    // 返回
    handleBackClick();
  };

  // 意见反馈按钮点击事件
  const handleFeedback = () => {
    window.location.href = 'mailto:erasaprotection@erasa.net';
  };

  return (
    <div className={`${styles.resultContainer} ${!isSearching ? styles.searchComplete : ''}`} ref={resultContainerRef}>
      {/* 显示搜索 loading */}
      {isSearching && (
        <>
          <ImageSearchResultSkeleton />
          {/* 只有 API 返回后才显示 ImageSearchLoading */}
          {showLoading && (
            <div className={`${styles.loadingWrapper} ${styles.fadeIn}`}>
              <div style={{ margin: 0 }}>
                <ImageSearchLoading
                  stepDuration={stepDuration}
                  searchMode={searchMode}
                  imageSearchUrl={imageSearchUrl}
                  taskId={taskId}
                  isNeedPay={isNeedPay}
                />
              </div>
            </div>
          )}
        </>
      )}

      {/* 根据搜索历史查询的搜索状态 */}
      {isQueryTaskStatus && <ImageSearchResultSkeleton />}

      {!isSearching && (
        <>
          {/* 搜索失败页 */}
          {isSearchFailed && (
            <NotFound
              onBackClick={handleBackClick}
              searchData={searchData}
              onTryAnotherFreeSearch={handleTryAnotherFreeSearch}
              onFeedback={handleFeedback}
            />
          )}

          {/* 搜索结果页 */}
          {!isSearchFailed && searchData && (
            <ImageSearchResult
              searchData={searchData}
              isLocked={isLocked}
              imageSearchUrl={imageSearchUrl}
              searchMode={searchMode}
              onBackClick={handleBackClick}
              contentFunction={contentFunction}
            />
          )}

          {/* 搜索报告页 */}
          {!isSearchFailed && !searchData && (
            <ImageSearchReport
              reportList={reportList}
              reportListLoading={reportListLoading}
              contentFunction={contentFunction}
            />
          )}
        </>
      )}
    </div>
  );
};

export default ImageSearchContainer;
